<template>
  <div class="sav-web">
    <!-- Nouveau ticket -->
    <v-dialog v-model="isCreateOpen" width="94%" max-width="760" persistent>
      <modal-ticket
        v-if="isCreateOpen"
        :clients="clients"
        :isClientsLoading="isClientsLoading"
        :formeOptions="formeOptions"
        :statutOptions="statutOptions"
        :userOptions="userOptions"
        :currentUser="currentUser"
        :isSaving="isSaving"
        @close="isCreateOpen = false"
        @create="onCreate"
        @reloadClients="loadClients(true)"
      ></modal-ticket>
    </v-dialog>

    <!-- Fenêtre d'édition d'un filtre : rien ne change avant « Appliquer » -->
    <v-dialog :model-value="!!editingKey" @update:model-value="(v) => { if (!v) editingKey = null }" width="460">
      <v-card class="sw-feditor-card">
        <div class="sw-feditor-head">
          <v-icon size="18" start color="white">{{ editingIcon }}</v-icon>
          {{ editingLabel }}
        </div>
        <div class="sw-feditor-body">
          <v-select v-if="editingKey === 'statut'" v-model="editDraft.statut" :items="statutOptions" label="Statut" multiple chips closable-chips variant="outlined" density="comfortable" hide-details></v-select>
          <v-select v-else-if="editingKey === 'forme'" v-model="editDraft.forme" :items="formeOptions" label="Forme" multiple chips closable-chips variant="outlined" density="comfortable" hide-details></v-select>
          <v-select v-else-if="editingKey === 'user'" v-model="editDraft.user" :items="userFilterOptions" label="Pris en charge par" clearable variant="outlined" density="comfortable" hide-details></v-select>
          <v-autocomplete v-else-if="editingKey === 'societe'" v-model="editDraft.societe" :items="societeOptions" label="Société" clearable variant="outlined" density="comfortable" hide-details no-data-text="Aucune société"></v-autocomplete>
          <v-select v-else-if="editingKey === 'periode'" v-model="editDraft.periode" :items="periodes" item-title="title" item-value="value" label="Créé" clearable variant="outlined" density="comfortable" hide-details></v-select>
        </div>
        <div class="sw-feditor-foot">
          <v-spacer></v-spacer>
          <v-btn variant="text" color="#607D8B" @click="editingKey = null">Fermer</v-btn>
          <v-btn variant="flat" color="#5C6BC0" style="color:white" @click="applyEditor">
            <v-icon start>mdi-check</v-icon> Appliquer
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Confirmation d'une action de masse (avec progression pendant l'envoi) -->
    <v-dialog v-model="bulkAsk.show" width="94%" max-width="440" :persistent="bulk.running">
      <v-card class="sw-feditor-card">
        <div class="sw-feditor-head">
          <v-icon size="18" start color="white" :icon="bulkAsk.icon"></v-icon>
          {{ bulkAsk.title }}
        </div>
        <div class="sw-feditor-body sw-bulk-d-body">
          <div class="sw-bulk-d-text">{{ bulkAsk.text }}</div>
          <div v-if="bulk.running" class="sw-bulk-d-progress">
            <v-progress-linear :model-value="bulk.total ? (100 * bulk.done) / bulk.total : 0" color="#3D4ED8" height="6" rounded></v-progress-linear>
            <span>Mise à jour {{ bulk.done }} / {{ bulk.total }}…</span>
          </div>
        </div>
        <div class="sw-feditor-foot">
          <v-spacer></v-spacer>
          <v-btn variant="text" color="#607D8B" :disabled="bulk.running" @click="bulkAsk.show = false">Annuler</v-btn>
          <v-btn variant="flat" color="#3D4ED8" style="color:white" :loading="bulk.running" :disabled="bulk.running" @click="runBulk">
            <v-icon start :icon="bulkAsk.icon"></v-icon> {{ bulkAsk.cta }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Détail en fenêtre (écrans moyens / petits) -->
    <v-dialog
      :model-value="!!selectedTicket && !isWide"
      @update:model-value="(v) => { if (!v) closeTicket() }"
      :fullscreen="isMobile"
      width="94%"
      max-width="780"
    >
      <ticket-detail
        v-if="selectedTicket && !isWide"
        v-bind="detailProps"
        :height="isMobile ? '100vh' : '86vh'"
        v-on="detailListeners"
      ></ticket-detail>
    </v-dialog>

    <!-- En-tête -->
    <div class="sw-head">
      <div class="sw-head-txt">
        <h1 class="sw-title">Tickets SAV</h1>
        <div class="sw-sub">Suivi des appels et des tickets clients · ACCEN Informatique</div>
      </div>
      <div class="sw-head-actions">
        <div class="sw-sync" title="Actualisation automatique toutes les 30 secondes">
          <span class="sw-sync-dot" :class="{ busy: isRefreshing }"></span>{{ syncLabel }}
        </div>
        <v-btn class="sw-btn-ghost" variant="flat" :loading="isRefreshing" title="Rafraîchir" @click="refreshNow">
          <v-icon start>mdi-refresh</v-icon> Rafraîchir
        </v-btn>
        <v-btn class="sw-btn-new" variant="flat" @click="openCreate">
          <v-icon start>mdi-plus-circle</v-icon> Nouveau ticket <span class="sw-kbd">N</span>
        </v-btn>
      </div>
    </div>

    <!-- Compteurs (cliquables = vues) -->
    <div class="sw-kpis">
      <button
        v-for="k in kpis"
        :key="k.key"
        type="button"
        class="sw-kpi"
        :class="{ on: activeView === k.key }"
        :title="k.title"
        @click="setView(k.key)"
      >
        <span class="sw-kpi-icon" :style="{ backgroundColor: k.color + '1A', color: k.color }"><v-icon size="18" :icon="k.icon"></v-icon></span>
        <span class="sw-kpi-txt">
          <span class="sw-kpi-value">{{ counts[k.count] }}</span>
          <span class="sw-kpi-label">{{ k.label }}</span>
        </span>
        <v-icon class="sw-kpi-chev" size="18">mdi-chevron-right</v-icon>
      </button>
    </div>

    <!-- Filtres : recherche + chips -->
    <v-card class="sw-filters" variant="flat">
      <div class="sw-fbar">
        <v-text-field
          ref="search"
          v-model="filters.search"
          placeholder="Rechercher (client, téléphone, demande, n° de ticket…)"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          class="sw-fsearch"
        >
          <template #append-inner><span class="sw-kbd">/</span></template>
        </v-text-field>
        <v-menu location="bottom end">
          <template #activator="{ props }">
            <v-btn v-bind="props" variant="flat" class="sw-addfilter">
              <v-icon start>mdi-filter-outline</v-icon> Filtre
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item v-for="f in addableFilters" :key="f.key" @click="editingKey = f.key">
              <template #prepend><v-icon size="18">{{ f.icon }}</v-icon></template>
              <v-list-item-title>{{ f.label }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!addableFilters.length" disabled>
              <v-list-item-title>Tous les filtres sont déjà actifs</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn variant="text" class="sw-reset" @click="resetFilters">
          <v-icon start>mdi-filter-remove-outline</v-icon> Réinitialiser
        </v-btn>
        <v-checkbox
          v-model="filters.global"
          label="Toutes les données"
          color="#5C6BC0"
          density="compact"
          hide-details
          class="sw-global"
          title="Cherche dans TOUS les tickets FileMaker (y compris anciens et clos), pas seulement ceux chargés"
        ></v-checkbox>
      </div>
      <div v-if="filters.global" class="sw-global-info">
        <v-icon size="15" color="#5C6BC0">mdi-database-search-outline</v-icon>
        <span v-if="!searchActive">Tapez au moins 2 caractères pour chercher dans tous les tickets (anciens et clos compris).</span>
        <span v-else-if="isSearching">Recherche dans toutes les données…</span>
        <span v-else>{{ serverResults.length }} ticket(s) trouvé(s) dans toutes les données<template v-if="serverResults.length >= 500"> — affinez la recherche (500 max)</template>.</span>
        <v-progress-circular v-if="isSearching" indeterminate size="13" width="2" color="#5C6BC0"></v-progress-circular>
      </div>
      <div class="sw-fchips" v-if="activeChips.length || hiddenMenuVisible">
        <button v-for="c in activeChips" :key="c.key" type="button" class="sw-chip" @click="editingKey = c.key">
          <v-icon size="14">{{ c.icon }}</v-icon>
          <span class="sw-chip-lab">{{ c.label }} :</span>
          <span class="sw-chip-val">{{ c.display }}</span>
          <v-icon size="15" class="sw-chip-x" @click.stop="clearFilter(c.key)">mdi-close</v-icon>
        </button>
        <!-- Expéditeurs masqués par défaut (HIDDEN_SENDERS dans le hook) : un par un -->
        <v-menu v-if="hiddenMenuVisible" location="bottom start" :close-on-content-click="false">
          <template #activator="{ props }">
            <button v-bind="props" type="button" class="sw-chip sw-chip-soft" title="Choisir les expéditeurs à afficher">
              <v-icon size="15" :icon="hiddenCount ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"></v-icon>
              <template v-if="hiddenCount">{{ hiddenCount }} mail{{ hiddenCount > 1 ? "s" : "" }} masqué{{ hiddenCount > 1 ? "s" : "" }}</template>
              <template v-else>Mails masqués : affichés</template>
              <span v-if="hiddenCount && shownSendersCount" class="sw-chip-val">· {{ shownSendersCount }} expéditeur{{ shownSendersCount > 1 ? "s" : "" }} affiché{{ shownSendersCount > 1 ? "s" : "" }}</span>
              <v-icon
                size="15"
                class="sw-chip-x"
                :title="hiddenCount ? 'Tout afficher' : 'Tout masquer à nouveau'"
                @click.stop="setAllSenders(!!hiddenCount)"
              >mdi-close</v-icon>
            </button>
          </template>
          <v-card class="sw-hidden-card">
            <div class="sw-hidden-title">Expéditeurs masqués par défaut</div>
            <div class="sw-hidden-sub">Cliquez sur un expéditeur pour afficher ou masquer ses mails.</div>
            <button
              v-for="s in hiddenSenderStats"
              :key="s.key"
              type="button"
              class="sw-hidden-row"
              :class="{ on: s.shown, empty: !s.count }"
              :title="s.shown ? 'Mails affichés — cliquer pour les masquer' : 'Mails masqués — cliquer pour les afficher'"
              @click="toggleSender(s.key)"
            >
              <v-icon size="18" class="sw-hidden-eye" :icon="s.shown ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"></v-icon>
              <span class="sw-hidden-mail">{{ s.sender }}</span>
              <span class="sw-hidden-count">{{ s.count }}</span>
            </button>
            <div class="sw-hidden-foot">
              <v-btn size="small" variant="text" color="#3949AB" :disabled="allSendersShown" @click="setAllSenders(true)">
                <v-icon start size="16">mdi-eye-outline</v-icon> Tout afficher
              </v-btn>
              <v-btn size="small" variant="text" color="#607D8B" :disabled="!shownSendersCount" @click="setAllSenders(false)">
                <v-icon start size="16">mdi-eye-off-outline</v-icon> Tout masquer
              </v-btn>
            </div>
          </v-card>
        </v-menu>
      </div>
    </v-card>

    <!-- 1er chargement -->
    <div v-if="isLoading" class="sw-state">
      <v-progress-circular indeterminate color="#5C6BC0" size="56" width="5"></v-progress-circular>
      <div class="sw-state-title">Chargement des tickets…</div>
    </div>

    <!-- Erreur de chargement -->
    <div v-else-if="loadError && !tickets.length" class="sw-state">
      <v-icon size="44" color="#EF5350">mdi-alert-circle-outline</v-icon>
      <div class="sw-state-title">Impossible de récupérer les tickets.</div>
      <v-btn variant="flat" color="#5C6BC0" style="color:white" @click="loadTickets()">
        <v-icon start>mdi-refresh</v-icon> Réessayer
      </v-btn>
    </div>

    <!-- Liste + détail -->
    <div v-else ref="mainArea" class="sw-main" :style="{ height: paneHeight + 'px' }">
      <v-card ref="listCard" class="sw-list-card" variant="flat">
        <div v-if="!tickets.length" class="sw-empty">
          <div class="sw-empty-badge"><v-icon size="34" color="white">mdi-headset</v-icon></div>
          <div class="sw-state-title">Aucun ticket pour l'instant</div>
          <div class="sw-state-sub">Un client appelle ? Ouvrez un ticket pour suivre sa demande.</div>
          <v-btn class="sw-btn-primary" variant="flat" @click="openCreate">
            <v-icon start>mdi-phone-plus</v-icon> Créer le premier ticket
          </v-btn>
        </div>
        <EasyDataTable
          v-else
          table-class-name="sw-table"
          alternating
          :headers="displayHeaders"
          :items="filteredTickets"
          :sort-by="'creeSort'"
          :sort-type="'desc'"
          :rows-per-page="50"
          :table-height="tableHeight"
          :body-row-class-name="rowClass"
          :header-item-class-name="headerClass"
          rows-per-page-message="Lignes par page"
          rows-of-page-separator-message="sur"
          empty-message="Aucun ticket ne correspond aux filtres"
          @click-row="onRowClick"
        >
          <!-- Case à cocher dans la colonne Statut (écart fixe, quel que soit le navigateur) :
               la zone de la case coche / décoche sans ouvrir le ticket -->
          <template #header-statut="header">
            <span class="sw-statut-head">
              <input
                type="checkbox"
                class="sw-check"
                :checked="allSel"
                :indeterminate="selCount > 0 && !allSel"
                :disabled="!filteredTickets.length"
                title="Tout sélectionner / tout désélectionner"
                aria-label="Tout sélectionner"
                @click.stop
                @change="toggleAllSel"
              />
              <span>{{ header.text }}</span>
            </span>
          </template>
          <template #item-statut="item">
            <div class="sw-statut-cell">
              <span class="sw-sel" @click.stop="toggleSel(item)">
                <input
                  type="checkbox"
                  class="sw-check"
                  :checked="!!sel[item.id]"
                  :aria-label="'Sélectionner le ticket ' + shortId(item.id)"
                  @click.stop
                  @change="toggleSel(item)"
                />
              </span>
              <span class="sw-pill" :style="{ backgroundColor: statutColor(item.statut) + '1F', color: statutColor(item.statut) }">
                <span class="sw-pill-dot"></span>{{ item.statut || "Sans statut" }}
              </span>
            </div>
          </template>

          <template #item-societe="item">
            <div class="sw-client">
              <span class="sw-client-ic" :class="'is-' + clientKind(item)"><v-icon size="16" :icon="clientIcon(item)"></v-icon></span>
              <!-- Pas de client : on affiche l'expéditeur de l'email à la place du « — » -->
              <div
                v-if="clientKind(item) === 'mail'"
                class="sw-stack sw-stack-mail"
                :title="[item.mail_expediteur_nom, item.mail_expediteur].filter(Boolean).join(' · ')"
              >
                <span class="sw-stack-main">{{ item.mail_expediteur || item.mail_expediteur_nom }}</span>
                <span class="sw-stack-sub">{{ [item.mail_expediteur ? item.mail_expediteur_nom : "", "#" + shortId(item.id)].filter(Boolean).join(" · ") }}</span>
              </div>
              <div v-else class="sw-stack" :title="item.societe">
                <span class="sw-stack-main">{{ item.societe || "—" }}</span>
                <span class="sw-stack-sub">{{ [item.client, "#" + shortId(item.id)].filter(Boolean).join(" · ") }}</span>
              </div>
            </div>
          </template>

          <template #item-telephone="item">
            <span class="sw-mono">{{ item.telephone || "—" }}</span>
          </template>

          <template #item-forme="item">
            <span v-if="item.forme" class="sw-forme">
              <v-icon size="15">{{ formeIcon(item.forme) }}</v-icon>{{ item.forme }}
            </span>
            <span v-else>—</span>
          </template>

          <!-- Demande et Résumé IA : 2 lignes, aperçu complet au survol de toute la cellule (.sw-hz) -->
          <template #item-commentaire="item">
            <div
              v-if="item.commentaire"
              class="sw-hz"
              @mouseenter="hoverIn($event, 'demande', item)"
              @mousemove="hoverMove"
              @mouseleave="hoverOut"
            >
              <div class="sw-clamp2">{{ preview(item.commentaire) }}</div>
            </div>
            <span v-else class="sw-ai-none">—</span>
          </template>

          <template #item-ia_resume="item">
            <div
              v-if="item.ia_resume"
              class="sw-hz"
              @mouseenter="hoverIn($event, 'ia', item)"
              @mousemove="hoverMove"
              @mouseleave="hoverOut"
            >
              <div class="sw-ai-cell">
                <v-icon size="14" class="sw-ai-ic">mdi-creation</v-icon>
                <span class="sw-ai-txt">{{ item.ia_resume }}</span>
              </div>
            </div>
            <span v-else class="sw-ai-none">—</span>
          </template>

          <template #item-user_interne="item">
            <span v-if="item.user_interne" class="sw-user">
              <span class="sw-avatar" :class="{ me: isMe(item.user_interne) }">{{ initials(item.user_interne) }}</span>
              <span class="sw-user-name">{{ item.user_interne }}</span>
            </span>
            <span v-else class="sw-unassigned">Non assigné</span>
          </template>

          <template #item-creeSort="item">
            <span v-if="ageInfo(item)" class="sw-age" :class="ageInfo(item).cls" :title="'Créé le ' + formatTs(item.creeMs)">{{ ageInfo(item).label }}</span>
            <span v-else class="sw-date" :title="'Créé le ' + formatTs(item.creeMs)">{{ formatShort(item.creeMs) }}</span>
          </template>

          <template #item-activite="item">
            <span class="sw-date sw-act" :title="formatTs(item.modifMs)">{{ relTime(item.modifMs) }}</span>
          </template>

          <!-- Menu d'actions « ⋯ » -->
          <template #item-actions="item">
            <v-menu location="bottom end">
              <template #activator="{ props }">
                <button v-bind="props" type="button" class="sw-more" title="Actions" @click.stop>
                  <v-icon size="18">mdi-dots-horizontal</v-icon>
                </button>
              </template>
              <v-list density="compact" class="sw-more-list">
                <v-list-item prepend-icon="mdi-open-in-app" title="Ouvrir le ticket" @click="selectTicket(item)"></v-list-item>
                <v-list-item
                  v-if="currentUser && !isMe(item.user_interne) && !isClosed(item.statut)"
                  prepend-icon="mdi-account-check-outline"
                  title="Prendre en charge"
                  :disabled="isSaving"
                  @click="quickTake(item)"
                ></v-list-item>
                <v-list-item v-if="!isClosed(item.statut)" prepend-icon="mdi-check" title="Résoudre" :disabled="isSaving" @click="quickResolve(item)"></v-list-item>
                <v-list-item v-else prepend-icon="mdi-restore" title="Rouvrir" :disabled="isSaving" @click="quickReopen(item)"></v-list-item>
              </v-list>
            </v-menu>
          </template>
        </EasyDataTable>
        <!-- Actions de masse : la barre remplace l'en-tête du tableau (sauf la case « tout ») -->
        <div v-if="selCount" class="sw-bulk" :class="{ compact: isSmall || (selectedTicket && isWide) }">
          <span class="sw-bulk-count">
            <input
              type="checkbox"
              class="sw-check"
              :checked="allSel"
              :indeterminate="!allSel"
              title="Tout sélectionner / tout désélectionner"
              aria-label="Tout sélectionner"
              @change="toggleAllSel"
            />
            <b>{{ selCount }}</b><span class="sw-bulk-ctxt">sélectionné{{ selCount > 1 ? "s" : "" }}</span>
            <button v-if="!allSel" type="button" class="sw-bulk-all" @click="selectAllVisible">
              Tout sélectionner ({{ filteredTickets.length }})
            </button>
          </span>
          <span class="sw-bulk-sp"></span>
          <button type="button" class="sw-bulk-btn" title="Résoudre" :disabled="bulk.running" @click="askBulk('statut', 'Résolu')">
            <v-icon size="16">mdi-check</v-icon><span class="sw-bulk-lbl">Résoudre</span>
          </button>
          <button type="button" class="sw-bulk-btn" title="Clôturer" :disabled="bulk.running" @click="askBulk('statut', 'Clos')">
            <v-icon size="16">mdi-archive-outline</v-icon><span class="sw-bulk-lbl">Clôturer</span>
          </button>
          <v-menu location="bottom end">
            <template #activator="{ props }">
              <button v-bind="props" type="button" class="sw-bulk-btn" title="Changer le statut" :disabled="bulk.running">
                <v-icon size="16">mdi-flag-outline</v-icon><span class="sw-bulk-lbl">Statut</span><v-icon size="14" class="sw-bulk-chev">mdi-chevron-down</v-icon>
              </button>
            </template>
            <v-list density="compact" class="sw-more-list sw-bulk-menu">
              <v-list-item v-for="s in statutOptions" :key="s" @click="askBulk('statut', s)">
                <template #prepend><span class="sw-bulk-dot" :style="{ backgroundColor: statutColor(s) }"></span></template>
                <v-list-item-title>{{ s }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-menu location="bottom end">
            <template #activator="{ props }">
              <button v-bind="props" type="button" class="sw-bulk-btn" title="Assigner" :disabled="bulk.running">
                <v-icon size="16">mdi-account-arrow-right-outline</v-icon><span class="sw-bulk-lbl">Assigner</span><v-icon size="14" class="sw-bulk-chev">mdi-chevron-down</v-icon>
              </button>
            </template>
            <v-list density="compact" class="sw-more-list sw-bulk-menu">
              <v-list-item v-for="u in userOptions" :key="u" @click="askBulk('assign', u)">
                <template #prepend><span class="sw-avatar sm" :class="{ me: isMe(u) }">{{ initials(u) }}</span></template>
                <v-list-item-title>{{ u }}<span v-if="isMe(u)" class="sw-bulk-me"> (moi)</span></v-list-item-title>
              </v-list-item>
              <v-divider class="my-1"></v-divider>
              <v-list-item prepend-icon="mdi-account-off-outline" title="Non assigné" @click="askBulk('assign', '')"></v-list-item>
            </v-list>
          </v-menu>
          <button type="button" class="sw-bulk-x" title="Tout désélectionner (Échap)" @click="clearSel">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <!-- Rappel en bas du tableau : des mails sont masqués -->
        <button
          v-if="tickets.length && hiddenCount"
          type="button"
          class="sw-foot-hidden"
          title="Tout afficher"
          @click="setAllSenders(true)"
        >
          <v-icon size="14">mdi-eye-off-outline</v-icon>
          {{ hiddenCount }} mail{{ hiddenCount > 1 ? "s" : "" }} masqué{{ hiddenCount > 1 ? "s" : "" }}
          <v-icon size="14" class="sw-foot-hidden-x">mdi-close</v-icon>
        </button>
      </v-card>

      <div v-if="selectedTicket && isWide" class="sw-pane">
        <ticket-detail v-bind="detailProps" :height="paneHeight" v-on="detailListeners"></ticket-detail>
      </div>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top">
      {{ snackbar.text }}
    </v-snackbar>

    <!-- Aperçu au survol de « Demande » / « Résumé IA » (une seule carte, à côté du pointeur de la souris) -->
    <teleport to="body">
      <div
        v-if="hover.show"
        ref="hovercard"
        class="sw-hovercard"
        :class="'is-' + hover.kind"
        :style="hover.style"
        @mouseenter="hoverKeep"
        @mouseleave="hoverOut"
        @wheel="onCardWheel"
      >
        <div class="sw-hc-head">
          <span class="sw-hc-ic"><v-icon size="15" :icon="hover.icon"></v-icon></span>
          <span class="sw-hc-title">{{ hover.title }}</span>
          <span v-if="hover.thread.length" class="sw-hc-pill">{{ hover.thread.length }} messages</span>
          <span class="sw-hc-meta">{{ hover.meta }}</span>
        </div>
        <div class="sw-hc-body" :class="{ 'is-thread': hover.thread.length }">
          <!-- Fil d'emails : une bulle par message, du plus ancien au plus récent (comme le détail) -->
          <template v-if="hover.thread.length">
            <div
              v-for="(m, i) in hover.thread"
              :key="'hm' + i"
              class="sw-ev sw-mail"
              :class="{ mine: m.internal, last: i === hover.thread.length - 1 }"
            >
              <div class="sw-mail-head">
                <span class="sw-avatar sm sw-mail-av" :style="{ backgroundColor: mailColor(m) }">{{ mailInitials(m) }}</span>
                <span class="sw-mail-who">
                  <span class="sw-mail-name">{{ m.name || m.email || "Expéditeur inconnu" }}</span>
                  <span v-if="m.name && m.email" class="sw-mail-addr">{{ m.email }}</span>
                </span>
                <span v-if="m.forwarded" class="sw-mail-tag">Transféré</span>
                <span v-if="m.date" class="sw-mail-date">{{ m.dateLabel || m.date }}</span>
              </div>
              <div v-if="m.to || m.cc || m.subject" class="sw-mail-info">
                <span v-if="m.to"><b>À</b> {{ m.to }}</span>
                <span v-if="m.cc"><b>Cc</b> {{ m.cc }}</span>
                <span v-if="m.subject"><b>Objet</b> {{ m.subject }}</span>
              </div>
              <div class="sw-ev-text">{{ m.body }}</div>
              <div v-if="m.signature" class="sw-mail-sign">{{ m.signature }}</div>
            </div>
          </template>
          <template v-else>
            <div v-if="hover.subject" class="sw-hc-subject">{{ hover.subject }}</div>
            <template v-for="(l, i) in hover.lines" :key="i">
              <div v-if="l.label" class="sw-hc-line"><b>{{ l.label }} :</b> {{ l.text }}</div>
              <div v-else class="sw-hc-line" :class="{ blank: !l.text }">{{ l.text }}</div>
            </template>
            <div v-if="hover.cut" class="sw-hc-more">…</div>
          </template>
        </div>
        <div class="sw-hc-foot"><v-icon size="13">mdi-cursor-default-click-outline</v-icon> Cliquez sur la ligne pour ouvrir le ticket</div>
      </div>
    </teleport>
  </div>
</template>

<script>
import useSavWeb, { PERIODES, UNASSIGNED, ECHANGE_TYPES, norm } from "../hooks/page-home";
import { parseMailThread, mailColor } from "../core/mailThread";
import ModalTicket from "./modal-ticket.vue";
import TicketDetail from "./ticket-detail.vue";

const FILTER_DEFS = [
  { key: "statut", label: "Statut", icon: "mdi-flag-outline" },
  { key: "forme", label: "Forme", icon: "mdi-phone-outline" },
  { key: "user", label: "Pris en charge par", icon: "mdi-account-outline" },
  { key: "societe", label: "Société", icon: "mdi-domain" },
  { key: "periode", label: "Date de création", icon: "mdi-calendar-range" },
];

export default {
  setup() {
    return { ...useSavWeb() };
  },
  data() {
    return {
      isCreateOpen: false,
      editingKey: null,
      editDraft: { statut: [], forme: [], user: null, societe: null, periode: null },
      paneHeight: 500,
      tableHeight: 400,
      periodes: PERIODES,
      // Aperçu au survol (Demande / Résumé IA)
      hover: { show: false, kind: "", icon: "", title: "", meta: "", subject: "", lines: [], cut: false, thread: [], style: {} },
      // Sélection multiple (id → true) et confirmation d'une action de masse
      sel: {},
      bulkAsk: { show: false, kind: "", value: "", icon: "", title: "", text: "", cta: "" },
      kpis: [
        { key: "a_traiter", label: "À traiter", count: "aTraiter", color: "#E5484D", icon: "mdi-inbox-arrow-down-outline", title: "Tickets non résolus / non clos" },
        { key: "non_assignes", label: "Non assignés", count: "nonAssignes", color: "#F08C00", icon: "mdi-account-question-outline", title: "Tickets à traiter que personne n'a pris en charge" },
        { key: "mes", label: "Mes tickets", count: "mes", color: "#4F5BD5", icon: "mdi-account-outline", title: "Mes tickets à traiter" },
        { key: "today", label: "Aujourd'hui", count: "today", color: "#2F9E44", icon: "mdi-calendar-today", title: "Tickets créés aujourd'hui" },
        { key: "retard", label: "En retard (> 24 h)", count: "retard", color: "#C92A2A", icon: "mdi-clock-alert-outline", title: "Non résolus depuis plus de 24 h (hors « en attente client »)" },
        { key: "tous", label: "Tous", count: "tous", color: "#495057", icon: "mdi-folder-multiple-outline", title: "Tickets chargés : tous les non clos + ceux résolus / clos depuis 30 jours" },
      ],
      headers: [
        { value: "statut", text: "Statut", sortable: true, width: 176 }, // + case à cocher (actions de masse)
        { value: "activite", text: "Activité", sortable: true, width: 112 },
        { value: "societe", text: "Client", sortable: true, width: 240 },
        // Demande et Résumé IA sans largeur fixe : ils se partagent la place restante.
        { value: "commentaire", text: "Demande", sortable: false },
        { value: "ia_resume", text: "Résumé IA", sortable: false },
        { value: "user_interne", text: "Pris en charge", sortable: true, width: 150 },
        { value: "creeSort", text: "Âge", sortable: true, width: 92 },
        { value: "actions", text: "", sortable: false, width: 52 },
      ],
    };
  },
  computed: {
    isWide() {
      return this.$vuetify.display.lgAndUp;
    },
    isMobile() {
      return this.$vuetify.display.xs;
    },
    isSmall() {
      return this.$vuetify.display.mdAndDown;
    },
    // Moins de colonnes quand le détail est ouvert à droite, ou sur petit écran.
    displayHeaders() {
      let keep = null;
      if (this.isSmall) keep = ["statut", "societe", "commentaire", "creeSort"];
      else if (this.selectedTicket && this.isWide) keep = ["statut", "societe", "commentaire", "creeSort", "actions"];
      return keep ? this.headers.filter((h) => keep.indexOf(h.value) !== -1) : this.headers;
    },
    // Tickets cochés (parmi ceux affichés) et état de la case « tout sélectionner »
    selTickets() {
      return this.filteredTickets.filter((t) => this.sel[t.id]);
    },
    selCount() {
      return this.selTickets.length;
    },
    allSel() {
      return this.selCount > 0 && this.selCount === this.filteredTickets.length;
    },
    // Pastille des expéditeurs masqués : visible s'ils ont des mails dans la vue
    // en cours, ou si l'un d'eux est affiché.
    hiddenMenuVisible() {
      return this.hiddenSenderStats.some((s) => s.count > 0 || s.shown);
    },
    shownSendersCount() {
      return this.hiddenSenderStats.filter((s) => s.shown).length;
    },
    allSendersShown() {
      return this.hiddenSenderStats.every((s) => s.shown);
    },
    // Compteur actif (vue courante).
    activeView() {
      const f = this.filters;
      if (f.user === UNASSIGNED) return "non_assignes";
      if (f.periode === "today") return "today";
      if (f.vue === "mes") return "mes";
      if (f.vue === "retard") return "retard";
      if (f.vue === "tous") return "tous";
      return "a_traiter";
    },
    activeChips() {
      const f = this.filters;
      const chips = [];
      if (f.statut && f.statut.length) chips.push({ key: "statut", label: "Statut", icon: "mdi-flag-outline", display: f.statut.join(", ") });
      if (f.forme && f.forme.length) chips.push({ key: "forme", label: "Forme", icon: "mdi-phone-outline", display: f.forme.join(", ") });
      if (f.user) chips.push({ key: "user", label: "Pris en charge", icon: "mdi-account-outline", display: f.user });
      if (f.societe) chips.push({ key: "societe", label: "Société", icon: "mdi-domain", display: f.societe });
      if (f.periode) {
        const p = PERIODES.find((x) => x.value === f.periode);
        chips.push({ key: "periode", label: "Créé", icon: "mdi-calendar-range", display: p ? p.title : f.periode });
      }
      return chips;
    },
    addableFilters() {
      const active = this.activeChips.map((c) => c.key);
      return FILTER_DEFS.filter((d) => active.indexOf(d.key) === -1);
    },
    editingLabel() {
      const d = FILTER_DEFS.find((x) => x.key === this.editingKey);
      return d ? d.label : "";
    },
    editingIcon() {
      const d = FILTER_DEFS.find((x) => x.key === this.editingKey);
      return d ? d.icon : "mdi-filter-variant";
    },
    userFilterOptions() {
      return [UNASSIGNED].concat(this.userOptions);
    },
    syncLabel() {
      const d = this.lastSync;
      if (!d) return "Synchro…";
      const p = (n) => (n < 10 ? "0" : "") + n;
      return "Synchro " + p(d.getHours()) + ":" + p(d.getMinutes()) + ":" + p(d.getSeconds());
    },
    // Propriétés / événements communs au détail (panneau à droite ou fenêtre).
    detailProps() {
      return {
        ticket: this.selectedTicket,
        client: this.selectedClient,
        echanges: this.echanges,
        isEchangesLoading: this.isEchangesLoading,
        isSaving: this.isSaving,
        isSending: this.isSending,
        statutOptions: this.statutOptions,
        formeOptions: this.formeOptions,
        userOptions: this.userOptions,
        currentUser: this.currentUser,
        echangeTypes: ECHANGE_TYPES,
        statutColor: this.statutColor,
        formatTs: this.formatTs,
        relTime: this.relTime,
        echangeMeta: this.echangeMeta,
        initials: this.initials,
        telHref: this.telHref,
        formeIcon: this.formeIcon,
        isClosed: this.isClosed,
        shortId: this.shortId,
      };
    },
    detailListeners() {
      return {
        close: () => this.closeTicket(),
        changeStatut: (s) => this.changeStatut(this.selectedTicket, s),
        assign: (u) => this.assignTicket(this.selectedTicket, u),
        take: () => this.takeTicket(this.selectedTicket),
        saveFields: (fields, done) => this.saveFields(this.selectedTicket, fields, done),
        addEchange: (p) => this.addEchange(this.selectedTicket, p.type, p.message, p.done),
      };
    },
  },
  watch: {
    // Sélection = tickets visibles : ceux qui sortent de la liste (filtre, vue,
    // ticket résolu par un autre poste…) sont décochés.
    filteredTickets(list) {
      const ids = Object.keys(this.sel);
      if (!ids.length) return;
      const keep = {};
      list.forEach((t) => { if (this.sel[t.id]) keep[t.id] = true; });
      if (Object.keys(keep).length !== ids.length) this.sel = keep;
    },
    // À l'ouverture de la fenêtre de filtre : copie des valeurs actuelles dans
    // le brouillon (les filtres ne changent qu'au clic sur « Appliquer »).
    editingKey(key) {
      if (!key) return;
      const f = this.filters;
      this.editDraft = {
        statut: Array.isArray(f.statut) ? f.statut.slice() : [],
        forme: Array.isArray(f.forme) ? f.forme.slice() : [],
        user: f.user || null,
        societe: f.societe || null,
        periode: f.periode || null,
      };
    },
  },
  mounted() {
    window.addEventListener("resize", this.computeLayout);
    window.addEventListener("keydown", this.onKey);
    // L'aperçu est positionné à l'écran : on le ferme dès qu'on fait défiler (sauf l'aperçu lui-même).
    window.addEventListener("scroll", this.onAnyScroll, true);
    this.computeLayout();
    setTimeout(this.computeLayout, 150);
    setTimeout(this.computeLayout, 500);
  },
  updated() {
    this.computeLayout();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.computeLayout);
    window.removeEventListener("keydown", this.onKey);
    window.removeEventListener("scroll", this.onAnyScroll, true);
    clearTimeout(this.hoverTimer);
  },
  methods: {
    // La zone liste/détail remplit l'écran : marge du bas = marge latérale.
    // Le tableau s'ajuste de lui-même (mesure de l'écart réel, comme Logs Web).
    computeLayout() {
      this.$nextTick(() => {
        const area = this.$refs.mainArea;
        if (!area || !area.getBoundingClientRect) return;
        const rect = area.getBoundingClientRect();
        const sideGap = Math.max(6, Math.round(rect.left));
        const desiredBottom = window.innerHeight - sideGap;
        const h = Math.max(320, Math.floor(desiredBottom - rect.top));
        if (Math.abs(h - this.paneHeight) > 1) this.paneHeight = h;
        const comp = this.$refs.listCard;
        const el = comp && comp.$el ? comp.$el : comp;
        if (!el || !el.getBoundingClientRect || !this.tickets.length) return;
        const delta = Math.floor(desiredBottom - el.getBoundingClientRect().bottom);
        if (Math.abs(delta) <= 1) return;
        this.tableHeight = Math.max(200, this.tableHeight + delta);
      });
    },
    setView(key) {
      const f = this.filters;
      f.user = null;
      f.periode = null;
      if (key === "non_assignes") { f.vue = "a_traiter"; f.user = UNASSIGNED; }
      else if (key === "today") { f.vue = "tous"; f.periode = "today"; }
      else f.vue = key;
    },
    applyEditor() {
      const k = this.editingKey;
      const f = this.filters;
      const d = this.editDraft;
      if (k === "statut") f.statut = d.statut.slice();
      else if (k === "forme") f.forme = d.forme.slice();
      else if (k === "user") f.user = d.user || null;
      else if (k === "societe") f.societe = d.societe || null;
      else if (k === "periode") f.periode = d.periode || null;
      this.editingKey = null;
    },
    clearFilter(key) {
      const f = this.filters;
      if (key === "statut" || key === "forme") f[key] = [];
      else f[key] = null;
    },
    openCreate() {
      this.isCreateOpen = true;
      if (!this.clients.length && !this.isClientsLoading) this.loadClients(false);
    },
    onCreate(form, done) {
      this.createTicket(form, (ok) => {
        if (ok) this.isCreateOpen = false;
        if (done) done(ok);
      });
    },
    onRowClick(item) {
      this.hideHover();
      this.selectTicket(item);
    },
    // Demande / Résumé IA prennent toute la place libre : les colonnes à largeur
    // fixe (case à cocher, Âge, ⋯…) ne s'élargissent plus, quel que soit le navigateur.
    headerClass(header) {
      if (header.value !== "commentaire" && header.value !== "ia_resume") return "";
      const both = this.displayHeaders.some((h) => h.value === "ia_resume") && this.displayHeaders.some((h) => h.value === "commentaire");
      return both ? "sw-col-grow2" : "sw-col-grow1";
    },
    rowClass(item) {
      const cls = [];
      if (item.id === this.selectedId) cls.push("sw-row-sel");
      if (this.sel[item.id]) cls.push("sw-row-chk");
      if (this.newIds.indexOf(item.id) !== -1) cls.push("sw-row-new");
      if (this.isClosed(item.statut)) cls.push("sw-row-closed");
      return cls.join(" ");
    },
    // ── Sélection multiple et actions de masse ─────────────────────────────
    toggleSel(item) {
      const s = Object.assign({}, this.sel);
      if (s[item.id]) delete s[item.id];
      else s[item.id] = true;
      this.sel = s;
    },
    // Case de l'en-tête : coche tous les tickets affichés (toutes les pages), ou décoche tout.
    toggleAllSel() {
      if (this.allSel) this.clearSel();
      else this.selectAllVisible();
    },
    selectAllVisible() {
      const s = {};
      this.filteredTickets.forEach((t) => { s[t.id] = true; });
      this.sel = s;
    },
    clearSel() {
      this.sel = {};
    },
    // Ouvre la confirmation d'une action de masse (kind : "statut" ou "assign").
    askBulk(kind, value) {
      const n = this.selCount;
      if (!n || this.bulk.running) return;
      const nb = n + " ticket" + (n > 1 ? "s" : "");
      let ask;
      if (kind === "statut") {
        const verb = norm(value) === norm("Résolu") ? "Résoudre" : norm(value) === norm("Clos") ? "Clôturer" : "Passer";
        ask = {
          icon: verb === "Résoudre" ? "mdi-check" : verb === "Clôturer" ? "mdi-archive-outline" : "mdi-flag-outline",
          title: verb === "Passer" ? "Passer " + nb + " en « " + value + " » ?" : verb + " " + nb + " ?",
          text: (n > 1 ? "Les " + nb + " sélectionnés passeront" : "Le ticket sélectionné passera") + " au statut « " + value + " ».",
          cta: verb === "Passer" ? "Changer le statut" : verb,
        };
      } else {
        ask = {
          icon: value ? "mdi-account-arrow-right-outline" : "mdi-account-off-outline",
          title: value ? "Assigner " + nb + " à " + value + " ?" : "Désassigner " + nb + " ?",
          text: value
            ? (n > 1 ? "Les " + nb + " sélectionnés seront pris en charge" : "Le ticket sélectionné sera pris en charge") + " par " + value + (this.isMe(value) ? " (vous)" : "") + "."
            : (n > 1 ? "Les " + nb + " sélectionnés n'auront plus" : "Le ticket sélectionné n'aura plus") + " de responsable.",
          cta: value ? "Assigner" : "Désassigner",
        };
      }
      ask.text += " Chaque changement est tracé dans l'historique du ticket.";
      this.bulkAsk = Object.assign({ show: true, kind: kind, value: value }, ask);
    },
    runBulk() {
      if (this.bulk.running) return;
      const a = this.bulkAsk;
      const list = this.selTickets.slice();
      const done = (okIds) => {
        // Tickets modifiés décochés ; ceux en échec restent cochés pour réessayer.
        const s = Object.assign({}, this.sel);
        okIds.forEach((id) => { delete s[id]; });
        this.sel = s;
        this.bulkAsk = Object.assign({}, this.bulkAsk, { show: false });
      };
      if (a.kind === "statut") this.bulkStatut(list, a.value, done);
      else this.bulkAssign(list, a.value, done);
    },
    quickTake(item) {
      const t = this.tickets.find((x) => x.id === item.id) || item;
      this.takeTicket(t);
    },
    quickResolve(item) {
      const t = this.tickets.find((x) => x.id === item.id) || item;
      this.changeStatut(t, "Résolu");
    },
    quickReopen(item) {
      const t = this.tickets.find((x) => x.id === item.id) || item;
      this.changeStatut(t, "Ouvert");
    },
    // Raccourcis : N = nouveau ticket, / = recherche, Échap = fermer le détail.
    onKey(e) {
      const tag = (e.target && e.target.tagName) || "";
      const typing = /INPUT|TEXTAREA|SELECT/.test(tag) || (e.target && e.target.isContentEditable);
      const dialogOpen = this.isCreateOpen || !!this.editingKey || this.bulkAsk.show;
      if (e.key === "Escape" && !typing && !dialogOpen && this.selectedTicket) { this.closeTicket(); return; }
      if (e.key === "Escape" && !typing && !dialogOpen && this.selCount) { this.clearSel(); return; }
      if (typing || dialogOpen || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "n" || e.key === "N") { e.preventDefault(); this.openCreate(); }
      else if (e.key === "/") {
        e.preventDefault();
        const f = this.$refs.search;
        if (f && f.focus) f.focus();
      }
    },
    isMe(name) {
      return !!name && norm(name) === norm(this.currentUser);
    },
    // Texte d'une cellule sur 2 lignes : retours à la ligne → espaces.
    preview(text) {
      return (text || "")
        .replace(/\[([^\]\n]+)\]\((?:https?:\/\/|mailto:)[^)\s]*\)/g, "$1")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 400);
    },
    // Icône du client : société, expéditeur d'un email, sinon personne.
    clientKind(item) {
      if (item.societe) return "soc";
      if (item.mail_expediteur || item.mail_expediteur_nom) return "mail";
      return "tel";
    },
    clientIcon(item) {
      const k = this.clientKind(item);
      if (k === "soc") return "mdi-office-building-outline";
      if (k === "mail") return "mdi-email-outline";
      return "mdi-account-outline";
    },

    // ── Aperçu au survol (Demande / Résumé IA) ─────────────────────────────
    hoverIn(e, kind, item) {
      clearTimeout(this.hoverTimer);
      const el = e.currentTarget;
      this.hoverMove(e);
      // Aperçu déjà ouvert (ou fermé à l'instant) : on passe d'une cellule à l'autre sans attendre.
      const warm = this.hover.show || Date.now() - (this.hoverClosedAt || 0) < 400;
      this.hoverTimer = setTimeout(() => this.showHover(el, kind, item), warm ? 60 : 120);
    },
    // Dernière position de la souris dans la cellule : l'aperçu s'ouvre à côté du pointeur.
    hoverMove(e) {
      this.hoverMouse = { x: e.clientX, y: e.clientY };
    },
    hoverOut() {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(this.hideHover, 150);
    },
    hoverKeep() {
      clearTimeout(this.hoverTimer);
    },
    hideHover() {
      clearTimeout(this.hoverTimer);
      if (this.hover.show) {
        this.hover.show = false;
        this.hoverClosedAt = Date.now();
      }
    },
    // Faire défiler le contenu de l'aperçu (texte long) ne doit pas le fermer.
    onAnyScroll(e) {
      const card = this.$refs.hovercard;
      if (card && e && e.target instanceof Node && card.contains(e.target)) return;
      this.hideHover();
      this.hoverClosedAt = 0; // après un défilement, délai normal avant le prochain aperçu
    },
    // Molette sur l'en-tête / le pied d'un aperçu long : fait défiler son texte, pas la page derrière.
    onCardWheel(e) {
      const card = this.$refs.hovercard;
      const body = card && card.querySelector(".sw-hc-body");
      if (!body || body.contains(e.target) || body.scrollHeight <= body.clientHeight) return;
      e.preventDefault();
      body.scrollTop += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    },
    mailColor, // couleur d'avatar par correspondant (core/mailThread, comme le détail)
    mailInitials(m) {
      return this.initials(m.name || (m.email || "").split("@")[0] || "?");
    },
    showHover(el, kind, item) {
      if (!el || !el.isConnected) return;
      const isIa = kind === "ia";
      // Demande contenant un fil d'emails (réponses, transferts) → bulles, comme dans le détail.
      let thread = [];
      if (!isIa && item.commentaire) {
        const addr = ((item.mail_expediteur || "").match(/[^\s<>"',;]+@[^\s<>"',;]+/) || [item.mail_expediteur])[0];
        let sender = item.mail_expediteur_nom || "";
        if (item.mail_expediteur) sender = sender ? sender + " <" + addr + ">" : item.mail_expediteur;
        const r = parseMailThread(item.commentaire, { sender: sender, date: this.formatTs(item.creeMs) });
        if (r.hasHeaders) thread = r.messages;
      }
      let text = String((isIa ? item.ia_resume : item.commentaire) || "")
        .replace(/\r\n?/g, "\n")
        .replace(/\[([^\]\n]+)\]\((?:https?:\/\/|mailto:)[^)\s]*\)/g, "$1")
        .replace(/^\s*\[(image|cid:[^\]]*)\]\s*$/gim, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      if (!text) return;
      // Résumé IA « sujet — détail » : le sujet passe en titre.
      let subject = "";
      if (isIa) {
        const i = text.indexOf(" — ");
        if (i > 0 && i < 200) {
          subject = text.slice(0, i).trim();
          text = text.slice(i + 3).trim();
        }
      }
      const MAX = 1500;
      const cut = text.length > MAX;
      if (cut) text = text.slice(0, MAX).replace(/\s+\S*$/, "");
      // « Libellé : valeur » (Script, Serveur, Base…) → libellé en gras.
      const lines = text.split("\n").map((l) => {
        const m = /^(?!https?)([A-Za-zÀ-ÖØ-öø-ÿ0-9'’ ._()-]{2,28}?)\s?:\s+(\S.*)$/.exec(l.trim());
        return m ? { label: m[1].trim(), text: m[2] } : { label: "", text: l.trim() };
      });
      const who = item.societe || item.mail_expediteur_nom || item.mail_expediteur || "";
      const meta = [who, "#" + this.shortId(item.id), this.relTime(item.creeMs)].filter(Boolean).join(" · ");

      // Position : à côté du pointeur de la souris (à droite, sinon à gauche),
      // calée en hauteur sur le pointeur. La carte ne suit pas la souris une fois
      // ouverte : on peut y entrer pour faire défiler le texte.
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = Math.min(480, vw - 24);
      const r = el.getBoundingClientRect();
      const m = this.hoverMouse || { x: r.left + 16, y: r.top + r.height / 2 };
      const OFF = 16; // écart entre le pointeur et la carte
      const maxH = (h) => Math.min(560, Math.max(160, h)) + "px";
      let left = m.x + OFF;
      if (left + width > vw - 12) left = m.x - OFF - width; // pas la place à droite → à gauche
      if (left < 12) left = Math.max(12, vw - width - 12); // écran étroit
      const style = { width: width + "px", left: left + "px" };
      if (m.y < vh / 2) {
        const top = Math.max(12, m.y - 18); // la carte descend depuis le pointeur
        style.top = top + "px";
        style.maxHeight = maxH(vh - top - 12);
      } else {
        const bottom = Math.max(12, vh - m.y - 18); // la carte remonte depuis le pointeur
        style.bottom = bottom + "px";
        style.maxHeight = maxH(vh - bottom - 12);
      }
      this.hover = {
        show: true,
        kind: kind,
        icon: isIa ? "mdi-creation" : this.formeIcon(item.forme),
        title: isIa ? "Résumé IA" : "Demande",
        meta: meta,
        subject: subject,
        lines: lines,
        cut: cut,
        thread: thread,
        style: style,
      };
      // Fil d'emails : on montre d'abord le message le plus récent (en bas), comme dans le détail.
      if (thread.length) {
        this.$nextTick(() => {
          const c = this.$refs.hovercard;
          const b = c && c.querySelector(".sw-hc-body");
          if (b) b.scrollTop = b.scrollHeight;
        });
      }
    },
  },
  components: {
    ModalTicket,
    TicketDetail,
  },
};
</script>

<style>
@import "../style.css";
</style>
